import React, { useState } from "react";
import { toast } from "react-toastify";
import { CloudUploadIcon, DeleteIcon } from "../../images/icons/icons";
import useCloudFrontUpload from "../../hooks/useCloudFrontUpload";
import { useSelector } from "react-redux";
import { selectToken } from "../auth/auth.selector";

const FileUploadComponent = ({
  maxFiles = 3,
  maxFileSize = 50, // in MB
  acceptedFormats = ["jpg", "jpeg", "png", "mp4"],
  onFilesChange,
  label = "Upload Files",
  hint = "Upload photos or videos",
  required = false,
  disabled = false,
  className = "",
}) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const { uploadToCloudfront, isUploading } = useCloudFrontUpload();
  const token = useSelector(selectToken);

  const handleFileInputChange = async (event) => {
    const files = Array.from(event.target.files);
    const newFiles = [];
    const uploadedUrls = { images: [], videos: [] };

    for (const file of files) {
      if (uploadedFiles.length + newFiles.length >= maxFiles) {
        toast.warn(`Maximum ${maxFiles} files allowed`);
        break;
      }

      if (file.size > maxFileSize * 1024 * 1024) {
        toast.error(
          `File ${file.name} is too large. Maximum size is ${maxFileSize}MB`
        );
        continue;
      }

      const fileType = file.name.split(".").pop()?.toLowerCase();
      if (!acceptedFormats.includes(fileType)) {
        toast.error(`File ${file.name} has unsupported format`);
        continue;
      }

      // Upload to CloudFront
      try {
        const uploadedUrl = await uploadToCloudfront(file, token);
        if (uploadedUrl) {
          const fileItem = {
            file: file,
            url: uploadedUrl,
            data_url: fileType === "mp4" ? null : URL.createObjectURL(file),
            type: fileType,
          };

          newFiles.push(fileItem);

          // Categorize for callback
          if (fileType === "mp4") {
            uploadedUrls.videos.push(uploadedUrl);
          } else {
            uploadedUrls.images.push(uploadedUrl);
          }
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (newFiles.length > 0) {
      const updatedFiles = [...uploadedFiles, ...newFiles];
      setUploadedFiles(updatedFiles);

      // Call parent callback with URLs
      if (onFilesChange) {
        const allImages = updatedFiles
          .filter((f) => f.type !== "mp4")
          .map((f) => f.url);
        const allVideos = updatedFiles
          .filter((f) => f.type === "mp4")
          .map((f) => f.url);
        onFilesChange({ images: allImages, videos: allVideos });
      }
    }

    // Reset input
    event.target.value = "";
  };

  const removeFile = (index) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);

    // Update parent callback
    if (onFilesChange) {
      const allImages = updatedFiles
        .filter((f) => f.type !== "mp4")
        .map((f) => f.url);
      const allVideos = updatedFiles
        .filter((f) => f.type === "mp4")
        .map((f) => f.url);
      onFilesChange({ images: allImages, videos: allVideos });
    }
  };

  const clearAllFiles = () => {
    setUploadedFiles([]);
    if (onFilesChange) {
      onFilesChange({ images: [], videos: [] });
    }
  };

  return (
    <div className={`file-upload-component ${className}`}>
      <label className={`section-label ${required ? "required" : ""}`}>
        {label}
        {required && <span className="required-star"> *</span>}
      </label>

      {hint && <p className="upload-hint">{hint}</p>}

      <div className="upload-area">
        <input
          type="file"
          id="fileUploadInput"
          multiple
          accept={acceptedFormats.map((format) => `.${format}`).join(",")}
          onChange={handleFileInputChange}
          style={{ display: "none" }}
          disabled={disabled || isUploading || uploadedFiles.length >= maxFiles}
        />

        {uploadedFiles.length === 0 ? (
          <div
            className="upload-placeholder"
            onClick={() =>
              !disabled &&
              !isUploading &&
              document.getElementById("fileUploadInput").click()
            }
          >
            <CloudUploadIcon />
            <p>
              {hint} (Max {maxFiles} files, up to {maxFileSize}MB each)
            </p>
            <small>
              <strong>Supported formats:</strong>{" "}
              {acceptedFormats.map((f) => f.toUpperCase()).join(", ")}
            </small>
          </div>
        ) : (
          <div className="upload-actions">
            <button
              type="button"
              className="add-more-btn"
              onClick={() => document.getElementById("fileUploadInput").click()}
              disabled={
                disabled || isUploading || uploadedFiles.length >= maxFiles
              }
            >
              Add More Files ({uploadedFiles.length}/{maxFiles})
            </button>
            <button
              type="button"
              className="clear-all-btn"
              onClick={clearAllFiles}
              disabled={disabled || isUploading}
            >
              Clear All
            </button>
          </div>
        )}

        {isUploading && (
          <div className="upload-progress">
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            ></div>
            Uploading files...
          </div>
        )}
      </div>

      {/* Display uploaded files */}
      {uploadedFiles.length > 0 && (
        <div className="uploaded-files-section">
          {uploadedFiles.map((fileItem, index) => {
            const fileName = fileItem.file?.name || `File ${index + 1}`;
            const fileSize = fileItem.file?.size
              ? (fileItem.file.size / (1024 * 1024)).toFixed(2) + " MB"
              : "Unknown size";
            const fileType = fileItem.type;

            return (
              <div key={index} className="uploaded-file">
                <div className="file-preview">
                  {fileType === "mp4" ? (
                    <div className="video-preview">
                      <svg
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="video-icon"
                      >
                        <path d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z" />
                      </svg>
                      <span className="video-label">VIDEO</span>
                    </div>
                  ) : fileItem.data_url ? (
                    <img
                      src={fileItem.data_url}
                      alt={fileName}
                      className="image-preview"
                    />
                  ) : (
                    <div className="image-placeholder">
                      <svg
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="image-icon"
                      >
                        <path d="M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="file-info">
                  <div className="file-details">
                    <div className="file-name">{fileName}</div>
                    <div className="file-size">{fileSize}</div>
                    <div className="file-status">✅ Uploaded</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="remove-file-btn"
                  title="Remove file"
                  disabled={disabled || isUploading}
                >
                  <DeleteIcon />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FileUploadComponent;
